import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getSitemap, getAudits, analyseUrl, recalculateScores } from '../api';
import { buildAuditRows, normUrl } from '../utils/auditRows';
import { normalizePageUrl } from '../utils/referencePages';
import { useToast } from '../ToastContext';

const SITEMAP_CACHE_TTL_MS = 5 * 60 * 1000;

export default function useSeoAudits(
  projectSlug,
  projectUrl,
  onAuditComplete,
  referencePages = [],
  dateDernierAudit = null,
) {
  const { showToast } = useToast();

  const [seoRows, setSeoRows] = useState([]);
  const [seoLoading, setSeoLoading] = useState(false);
  const [seoError, setSeoError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [urls, setUrls] = useState([]);
  const [sitemapUrls, setSitemapUrls] = useState([]);
  const [sitemapLoading, setSitemapLoading] = useState(false);
  const [auditsLoading, setAuditsLoading] = useState(false);
  const [currentUrl, setCurrentUrl] = useState('');
  const [sort, setSort] = useState({ key: 'url', dir: 'asc' });
  const [rowLoading, setRowLoading] = useState({});

  const mountedRef = useRef(true);
  const cancelledRef = useRef(false);
  const sitemapCacheRef = useRef({ slug: null, urls: null, fetchedAt: 0 });

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const deduplicatePages = useCallback((pages) => {
    if (!projectUrl) return pages;
    const urlMap = new Map();
    let mainUrl = projectUrl.trim();
    if (!mainUrl.startsWith('http')) mainUrl = `https://${mainUrl}`;
    const cleanMainUrl = normUrl(mainUrl);

    pages.forEach((page) => {
      const normalized = normUrl(page.url);
      const key = normalized === cleanMainUrl ? cleanMainUrl : normalized;
      if (!urlMap.has(key)) {
        urlMap.set(key, normalized === cleanMainUrl ? { ...page, url: cleanMainUrl } : page);
      }
    });
    return Array.from(urlMap.values());
  }, [projectUrl]);

  const referencePagesKey = (referencePages || [])
    .map((u) => normalizePageUrl(u))
    .filter(Boolean)
    .sort()
    .join('|');

  const buildRowsFromUrlsAndAudits = useCallback(
    (baseUrls, audits) => {
      const { urls: urlsToUse, rows } = buildAuditRows(baseUrls, audits, projectUrl, {
        missingLoading: false,
        prependHome: true,
        extraUrls: referencePages,
        orphanAudits: 'reference-only',
      });
      return { urlsToUse, rows };
    },
    [projectUrl, referencePagesKey],
  );

  const fetchSitemapUrls = useCallback(async (force = false) => {
    if (!projectSlug) return [];
    const cache = sitemapCacheRef.current;
    if (
      !force
      && cache.slug === projectSlug
      && cache.urls
      && Date.now() - cache.fetchedAt < SITEMAP_CACHE_TTL_MS
    ) {
      return cache.urls;
    }
    const res = await getSitemap(projectSlug).catch(() => ({ urls: [] }));
    const list = res.urls || [];
    sitemapCacheRef.current = { slug: projectSlug, urls: list, fetchedAt: Date.now() };
    return list;
  }, [projectSlug]);

  // Sitemap : une fois par projet (cache 5 min), pas à chaque changement d'audit
  useEffect(() => {
    if (!projectSlug) return;
    let cancelled = false;
    setSitemapLoading(true);

    fetchSitemapUrls()
      .then((list) => {
        if (!cancelled && mountedRef.current) setSitemapUrls(list);
      })
      .catch(() => {
        if (!cancelled && mountedRef.current) setSitemapUrls([]);
      })
      .finally(() => {
        if (!cancelled && mountedRef.current) setSitemapLoading(false);
      });

    return () => { cancelled = true; };
  }, [projectSlug, fetchSitemapUrls]);

  // Audits : léger, rechargé quand la date d'audit ou les pages de référence changent
  useEffect(() => {
    if (!projectSlug || sitemapLoading) return;
    let cancelled = false;
    setAuditsLoading(true);

    getAudits(projectSlug)
      .then((audits) => {
        if (cancelled || !mountedRef.current) return;
        const { urlsToUse, rows } = buildRowsFromUrlsAndAudits(sitemapUrls, audits);
        setUrls(urlsToUse);
        setSeoRows(rows);
        setSeoError(null);
      })
      .catch((err) => {
        if (!cancelled && mountedRef.current) {
          console.error(err);
          setSeoRows([]);
          setSeoError('Erreur lors du chargement des audits');
        }
      })
      .finally(() => {
        if (!cancelled && mountedRef.current) setAuditsLoading(false);
      });

    return () => { cancelled = true; };
  }, [projectSlug, sitemapUrls, sitemapLoading, buildRowsFromUrlsAndAudits, dateDernierAudit, referencePagesKey]);

  const handleAnalyseOne = async (url) => {
    if (!projectSlug || !url) {
      showToast('Erreur: paramètres manquants', 'error');
      return;
    }

    setRowLoading((prev) => ({ ...prev, [url]: true }));
    setSeoRows((prev) => prev.map((r) => (r.url === url ? { ...r, loading: true } : r)));

    try {
      const result = await analyseUrl(projectSlug, url, true);
      if (!mountedRef.current) return;
      setSeoRows((prev) => prev.map((r) => (r.url === url ? { ...result, url, loading: false } : r)));

      try {
        await recalculateScores(projectSlug);
        const audits = await getAudits(projectSlug);
        if (!mountedRef.current) return;
        const auditMap = {};
        audits.forEach((a) => {
          const key = normalizePageUrl(a.url);
          if (key) auditMap[key] = a;
        });
        setSeoRows((prev) => prev.map((row) => {
          const updated = auditMap[normalizePageUrl(row.url)];
          return updated ? { ...updated, url: row.url, loading: false } : row;
        }));
      } catch (e) {
        console.error(e);
      }
      showToast('Analyse terminée', 'success');
      if (onAuditComplete) onAuditComplete();
    } catch (err) {
      if (!mountedRef.current) return;
      const msg = err.response?.data?.detail || err.message || 'Erreur';
      setSeoRows((prev) => prev.map((r) => (r.url === url ? { ...r, loading: false, error: true, errorMsg: msg } : r)));
      showToast(msg, 'error');
    } finally {
      if (mountedRef.current) {
        setRowLoading((prev) => {
          const next = { ...prev };
          delete next[url];
          return next;
        });
      }
    }
  };

  const handleAnalyseAll = async () => {
    setDone(false);
    if (!urls.length) return;
    cancelledRef.current = false;
    setSeoLoading(true);
    setCurrentUrl('');
    setProgress(0);

    try {
      for (let i = 0; i < urls.length; i++) {
        if (cancelledRef.current || !mountedRef.current) break;
        const url = urls[i];
        setCurrentUrl(url);
        setProgress(Math.round((i / urls.length) * 100));

        try {
          const seo = await analyseUrl(projectSlug, url, true);
          if (mountedRef.current) {
            setSeoRows((prev) => prev.map((r) => (r.url === url ? { ...seo, url, loading: false } : r)));
          }
          setProgress(Math.round(((i + 1) / urls.length) * 100));
          await new Promise((r) => setTimeout(r, 300));
        } catch (err) {
          if (mountedRef.current) {
            setSeoRows((prev) => prev.map((r) => (r.url === url ? { ...r, loading: false, error: true, errorMsg: err.message } : r)));
          }
        }
      }

      if (mountedRef.current) {
        try {
          await recalculateScores(projectSlug);
          const audits = await getAudits(projectSlug);
          if (!mountedRef.current) return;
          const auditMap = {};
          audits.forEach((a) => {
            const key = normalizePageUrl(a.url);
            if (key) auditMap[key] = a;
          });
          const rows = urls.map((u) => {
            const updated = auditMap[normalizePageUrl(u)];
            return updated
              ? { ...updated, url: u, loading: false }
              : { url: u, loading: false };
          });
          setSeoRows(rows);
        } catch (e) {
          console.error(e);
        }

        setCurrentUrl('');
        setDone(true);
        if (onAuditComplete) onAuditComplete();
      }
    } catch (e) {
      if (mountedRef.current) setSeoError(e.message || 'Erreur analyse');
    } finally {
      if (mountedRef.current) {
        setSeoLoading(false);
        setTimeout(() => { if (mountedRef.current) setCurrentUrl(''); }, 2000);
        setProgress(0);
      }
    }
  };

  const refetchSeoData = useCallback(async () => {
    if (!projectSlug) return;
    try {
      const [sitemap, audits] = await Promise.all([
        fetchSitemapUrls(true),
        getAudits(projectSlug),
      ]);
      if (!mountedRef.current) return;
      setSitemapUrls(sitemap);
      const { urlsToUse, rows } = buildRowsFromUrlsAndAudits(sitemap, audits);
      setUrls(urlsToUse);
      setSeoRows(rows);
      setSeoError(null);
    } catch (err) {
      console.error(err);
    }
  }, [projectSlug, fetchSitemapUrls, buildRowsFromUrlsAndAudits]);

  const getSortValue = useCallback((page, key) => {
    if (key === 'url') {
      const u = page.url || '';
      const normalized = normUrl(u);
      let mainUrl = (projectUrl || '').trim();
      if (mainUrl && !mainUrl.startsWith('http')) mainUrl = `https://${mainUrl}`;
      if (normalized === normUrl(mainUrl)) return '';
      return normalized;
    }
    const val = page[key];
    if (val === null || val === undefined || val === 0) return -1;
    return val;
  }, [projectUrl]);

  const handleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      return { key, dir: 'desc' };
    });
  };

  const sortedRows = useMemo(() => {
    const pages = deduplicatePages([...seoRows]);
    return pages.sort((a, b) => {
      const vA = getSortValue(a, sort.key);
      const vB = getSortValue(b, sort.key);
      if (vA === vB) return 0;
      if (sort.dir === 'asc') return vA > vB ? 1 : -1;
      return vA < vB ? 1 : -1;
    });
  }, [seoRows, deduplicatePages, getSortValue, sort.key, sort.dir]);

  return {
    seoRows,
    setSeoRows,
    sortedRows,
    seoLoading,
    seoError,
    progress,
    done,
    urls,
    sitemapLoading,
    auditsLoading,
    currentUrl,
    sort,
    rowLoading,
    handleAnalyseOne,
    handleAnalyseAll,
    handleSort,
    refetchSeoData,
  };
}
